import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { Fragment, useMemo, useState } from "react";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import {
  CalendarFilledIcon,
  ClockIcon,
  PlaceIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  convertDateStringToObj,
  formatHHMM,
} from "@whoosh/utilities/src/dates";

import { EventLocationsModal } from "../EventDateTimeLocationBar/EventLocationsModal";
import { EventDetailMultiSessionDateDisplay_event$key } from "./__generated__/EventDetailMultiSessionDateDisplay_event.graphql";

type EventDetailMultiSessionDateDisplayProps = {
  fragmentRef: EventDetailMultiSessionDateDisplay_event$key;
};

const RowSeparator = () => (
  <Box
    height={0}
    borderBottomWidth={1}
    borderColor="outline-primary"
    opacity={0.3}
    marginVertical={2}
  />
);

export const EventDetailMultiSessionDateDisplay = ({
  fragmentRef,
}: EventDetailMultiSessionDateDisplayProps) => {
  const data = useFragment(
    EventDetailMultiSessionDateDisplayFragmentString,
    fragmentRef
  );

  const [locationsModalOpen, setLocationsModalOpen] = useState(false);
  const [modalLocations, setModalLocations] = useState<
    { id: string; name: string; bucketName?: string | null }[]
  >([]);

  const sessions = useMemo(
    () =>
      data.eventSessions?.edges?.flatMap((e) => {
        const node = e?.node;
        if (!node) return [];

        const dateObj =
          node.agenda.date && convertDateStringToObj(node.agenda.date);

        const formattedDate = dateObj
          ? format(dateObj, "EEE, MMM do, yyyy")
          : "";

        const formattedStartTime = node.startAt && formatHHMM(node.startAt);
        const formattedEndTime = node.endAt ? formatHHMM(node.endAt) : "";
        const time = node.displayTime
          ? node.displayTime
          : `${formattedStartTime} - ${formattedEndTime}`;

        const courses =
          node.courses?.edges?.flatMap((c) =>
            c?.node?.id
              ? [
                  {
                    id: c.node.id,
                    name: c.node.name,
                    bucketName: c.node.courseBucket?.name ?? null,
                  },
                ]
              : []
          ) ?? [];

        return [{ id: node.id, date: formattedDate, time, courses }];
      }) ?? [],
    [data.eventSessions?.edges]
  );

  const handleLocationsPress = (
    courses: { id: string; name: string; bucketName: string | null }[]
  ) => {
    setModalLocations(courses);
    setLocationsModalOpen(true);
  };

  return (
    <>
      <Box
        backgroundColor="surface-secondary"
        borderRadius="2"
        padding={4}
        flexDirection="row"
        gap={4}
      >
        {/* Date column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <CalendarFilledIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Date`}
            </Text>
          </HStack>
          {sessions.map((session, index) => (
            <Fragment key={session.id}>
              {index > 0 && <RowSeparator />}
              <Text variant="mainSmallSemiBold" color="text-primary">
                {session.date}
              </Text>
            </Fragment>
          ))}
        </VStack>

        {/* Time column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <ClockIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Time`}
            </Text>
          </HStack>
          {sessions.map((session, index) => (
            <Fragment key={session.id}>
              {index > 0 && <RowSeparator />}
              <Text variant="mainSmallSemiBold" color="text-primary">
                {session.time}
              </Text>
            </Fragment>
          ))}
        </VStack>

        {/* Location column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <PlaceIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Location`}
            </Text>
          </HStack>
          {sessions.map((session, index) => {
            const hasMultipleLocations = session.courses.length > 1;
            return (
              <Fragment key={session.id}>
                {index > 0 && <RowSeparator />}
                {hasMultipleLocations ? (
                  <Pressable
                    onPress={() => handleLocationsPress(session.courses)}
                  >
                    <Text
                      variant="mainSmallSemiBold"
                      color="text-primary"
                      textDecorationLine="underline"
                    >
                      {`${session.courses.length} ${t`locations`}`}
                    </Text>
                  </Pressable>
                ) : (
                  <Text variant="mainSmallSemiBold" color="text-primary">
                    {session.courses[0]?.name ?? ""}
                  </Text>
                )}
              </Fragment>
            );
          })}
        </VStack>
      </Box>

      <EventLocationsModal
        isOpen={locationsModalOpen}
        onClose={() => setLocationsModalOpen(false)}
        locations={modalLocations}
      />
    </>
  );
};

export const EventDetailMultiSessionDateDisplayFragmentString = graphql`
  fragment EventDetailMultiSessionDateDisplay_event on Event {
    eventSessions(first: 1000) {
      edges {
        node {
          id
          startAt
          endAt
          displayTime
          courses {
            edges {
              node {
                id
                name
                courseBucket {
                  name
                }
              }
            }
          }
          agenda {
            date
          }
        }
      }
    }
  }
`;
